import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { getMedias } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { MediaLightbox } from '../../medias/public';
import { formatFileSize, getFileIcon } from '../../medias/public/mediaDisplay';
import { isImageMimeOrExtension, isVideoMimeOrExtension } from '../../medias/public/mediaMime';
import { mediasToCarouselItems } from '../../medias/public/mediaScopeUtils';
import { buildOrderMediaCollectionKey, collectCandidateMediaIds } from './orderMediaCandidates';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function fileExtension(filename) {
  const m = /\.([a-z0-9]{1,6})$/i.exec(String(filename || ''));
  return m ? m[1].toUpperCase() : '';
}

function toLightboxMedia(item) {
  return {
    id: item.id,
    mime_type: item.mime_type,
    filename: item.filename || '',
    file_path: item.file_path || '',
    file_size: item.file_size ?? 0,
  };
}

function MediaPreview({ item, fit = 'contain', iconSize = 56 }) {
  if (isImageMimeOrExtension(item.mime_type, item.filename)) {
    return <Box component="img" src={item.url} alt={item.filename || ''} sx={{ width: '100%', height: '100%', objectFit: fit, display: 'block' }} />;
  }
  if (isVideoMimeOrExtension(item.mime_type, item.filename)) {
    return (
      <Box component="video" src={item.url} muted playsInline preload="metadata" controls={fit === 'contain'} sx={{ width: '100%', height: '100%', objectFit: fit, display: 'block' }} />
    );
  }
  return getFileIcon(item.mime_type, item.filename, iconSize);
}

/**
 * Galeria do pedido: visualizador grande com setas (← → no teclado) e miniaturas ao lado.
 * Arquivos que não são imagem/vídeo (3MF, STL, OBJ, PDF…) aparecem como cartão com download.
 */
export default function OrderMediaRail({
  orderId,
  order,
  formItems,
  refreshKey = 0,
  cardSx,
  headingSx,
  onManage,
}) {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const orderRef = useRef(order);
  const formItemsRef = useRef(formItems);
  const rootRef = useRef(null);
  orderRef.current = order;
  formItemsRef.current = formItems;

  const collectionKey = useMemo(
    () => buildOrderMediaCollectionKey(order, formItems, orderId, refreshKey),
    [order, formItems, orderId, refreshKey],
  );

  useEffect(() => {
    if (!orderId) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getMedias();
        const all = res.data?.medias || [];
        const ids = collectCandidateMediaIds(orderRef.current, formItemsRef.current, orderId, all);
        const medias = [...ids]
          .map((id) => all.find((m) => m.id === id))
          .filter(Boolean)
          .sort((a, b) => String(a.filename || a.id).localeCompare(String(b.filename || b.id)));
        if (!cancelled) {
          setItems(mediasToCarouselItems(medias).map((it, i) => ({ ...it, file_size: medias[i]?.file_size ?? 0 })));
        }
      } catch {
        if (!cancelled) {
          setItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId, collectionKey]);

  const safeIndex = items.length ? Math.min(activeIndex, items.length - 1) : 0;
  const active = items[safeIndex] || null;
  const activeIsVisual = active
    ? isImageMimeOrExtension(active.mime_type, active.filename) || isVideoMimeOrExtension(active.mime_type, active.filename)
    : false;

  const goBy = useCallback(
    (delta) => {
      if (items.length <= 1) return;
      setActiveIndex((prev) => (prev + delta + items.length) % items.length);
    },
    [items.length],
  );

  const handleKeyDown = (e) => {
    if (e.target !== rootRef.current && e.target?.closest?.('input, textarea, select')) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goBy(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goBy(1);
    }
  };

  if (!orderId) {
    return null;
  }

  const downloadHref = active ? `${API_URL}${active.file_path}` : undefined;
  const lightboxItems = items.map(toLightboxMedia);

  return (
    <Paper elevation={0} sx={cardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography component="h2" sx={{ ...headingSx, mb: 0 }} id="order-rail-media-h">
          {translate('orders.media_rail_title')}
        </Typography>
        {items.length > 0 ? (
          <Typography component="span" variant="body2" color="text.secondary">
            · {items.length}
          </Typography>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {onManage ? (
          <Button size="small" variant="outlined" startIcon={<PhotoLibraryIcon fontSize="small" />} onClick={onManage}>
            {translate('orders.gallery_manage_chip')}
          </Button>
        ) : null}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{ py: 3, textAlign: 'center', color: 'text.secondary', bgcolor: 'action.hover', borderRadius: 1, fontSize: '0.875rem', cursor: onManage ? 'pointer' : 'default' }}
          onClick={onManage}
        >
          {translate('media.no_images')}
        </Box>
      ) : (
        <Box
          ref={rootRef}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          aria-labelledby="order-rail-media-h"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) 200px' },
            gap: 1.5,
            outline: 'none',
          }}
        >
          <Box sx={{ position: 'relative', aspectRatio: '16 / 10', bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
            {activeIsVisual ? (
              <Box sx={{ width: '100%', height: '100%', cursor: 'zoom-in' }} onClick={() => setLightboxOpen(true)}>
                <MediaPreview item={active} />
              </Box>
            ) : (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.25, p: 2, textAlign: 'center' }}>
                {getFileIcon(active.mime_type, active.filename, 64)}
                <Box>
                  <Typography sx={{ fontWeight: 600, wordBreak: 'break-all' }}>{active.filename}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[fileExtension(active.filename) || active.mime_type, active.file_size ? formatFileSize(active.file_size) : null].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Button variant="contained" size="small" startIcon={<DownloadIcon />} component="a" href={downloadHref} download={active.filename}>
                    {translate('media.download_file')}
                  </Button>
                  <Button variant="outlined" size="small" startIcon={<OpenInFullIcon fontSize="small" />} onClick={() => setLightboxOpen(true)}>
                    {translate('orders.gallery_open_file')}
                  </Button>
                </Box>
              </Box>
            )}
            <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
              <Tooltip title={translate('media.download_file')}>
                <IconButton size="small" component="a" href={downloadHref} download={active.filename} sx={{ bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } }}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {items.length > 1 ? (
              <>
                <IconButton
                  size="small"
                  aria-label={translate('media.preview_previous')}
                  onClick={() => goBy(-1)}
                  sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={translate('media.preview_next')}
                  onClick={() => goBy(1)}
                  sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
                <Box sx={{ position: 'absolute', bottom: 8, left: 8, px: 1, py: 0.25, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '0.75rem' }}>
                  {safeIndex + 1} / {items.length}
                </Box>
              </>
            ) : null}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(5, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: 0.75,
              alignContent: 'start',
              maxHeight: { sm: 340 },
              overflowY: 'auto',
            }}
          >
            {items.map((item, index) => {
              const visual = isImageMimeOrExtension(item.mime_type, item.filename) || isVideoMimeOrExtension(item.mime_type, item.filename);
              const ext = fileExtension(item.filename);
              return (
                <Tooltip key={item.id} title={item.filename || ''} placement="top">
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    sx={{
                      position: 'relative',
                      aspectRatio: '1',
                      p: 0,
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: 2,
                      borderColor: index === safeIndex ? 'primary.main' : 'transparent',
                      bgcolor: 'action.hover',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {visual ? <MediaPreview item={item} fit="cover" /> : getFileIcon(item.mime_type, item.filename, 24)}
                    {!visual && ext ? (
                      <Box sx={{ position: 'absolute', left: 3, bottom: 3, px: 0.5, borderRadius: 0.5, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '0.625rem', fontWeight: 700, lineHeight: 1.5 }}>
                        {ext}
                      </Box>
                    ) : null}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}

      {lightboxOpen && active ? (
        <MediaLightbox
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          items={lightboxItems}
          activeMedia={toLightboxMedia(active)}
          onNavigate={(media) => {
            const idx = items.findIndex((m) => m.id === media.id);
            if (idx >= 0) setActiveIndex(idx);
          }}
          apiBaseUrl={API_URL}
          desktopOpenContext={{ domain: 'order', entityId: orderId }}
        />
      ) : null}
    </Paper>
  );
}
